import React, { useState, useEffect } from "react";
import { Container, Row, Stack } from "react-bootstrap";
import { useTranslation, Trans } from "react-i18next";
import { Badge, Flex } from "antd";
import { ArrivalCard } from "./ArrivalCard";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AirplaneLanding01Icon,
  FilterIcon,
  Search01Icon,
  Airplane02Icon,
  Alert02Icon,
} from "@hugeicons/core-free-icons";
import { AntInput } from "../../../CommonElements/AntInput";
import BtnAsLink from "../../../Component/BtnAsLink";
import {
  useGetAirportArrivalsQuery,
  useMarkAsArrivedMutation,
  useCancelFlightMutation,
  useRestoreFlightMutation,
} from "../../../services/arrivals";
import { useDebouncedSearch } from "../../../utils/hooks/useDebouncedSearch";
import ArrivalCardSkeleton from "./ArrivalCardSkeleton";
import { ReceptionCardModal } from "./ReceptionCardModal";
import ConfirmModal from "../../../CommonElements/ConfirmModal";
import { Button } from "react-bootstrap";
import { BusStatusSection } from "./BusStatusSection";
import { useArrival } from "./hooks/useArrival";
import { useParams, useSearchParams } from "react-router-dom";
import { GenericFilterModal } from "../../../CommonElements/TableC/GenericFilterModal";
import { useFilter } from "../../../CommonElements/TableC/hooks/useFilter";
import { Radio } from "antd";
import { AntPagination } from "@/Component/AntPagination/AntPagination";
import { SearchFilter } from "../../../CommonElements/SearchFilter/SearchFilter";
import { useSystem } from "@/utils/hooks/useLocation";
import { permissions } from "@/utils/constants";
import usePermissions from "@/utils/hooks/usePermissions";

const AirportReceptionList = ({ activeSource: activeSourceProp } = {}) => {
  const { t } = useTranslation();
  const {
    filters,
    isFilterOpen,
    setIsFilterOpen,
    handleFilterApply,
    handleFilterReset,
  } = useFilter();
  const [nusukFilters, setNusukFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedArrival, setSelectedArrival] = useState(null);
  const [selectedArrivalIndex, setSelectedArrivalIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);

  const { from_id } = useSystem();
  const {
    hasAnyPermission
  } = usePermissions();

  const { port } = useParams();

  const [searchParams] = useSearchParams();
  const source =
    activeSourceProp ?? searchParams.get("source") ?? "delegation";
  const isNusuk = source === "nusuk";
  const canControlDelegation = hasAnyPermission([
    permissions.control_port_reception_cards_from_missions
  ]);
  const canControlNusuk = hasAnyPermission([
    permissions.controle_port_reception_cards_from_nusk
  ]);
  const markAsArrivedMutation = useMarkAsArrivedMutation();
  const cancelFlightMutation = useCancelFlightMutation();
  const restoreFlightMutation = useRestoreFlightMutation();
  // Use debounced search hook
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );
  const [dateQuery, setDateQuery] = useState(
    searchParams.get("date") || "today"
  );

  // Nusuk filter handler (like Madina reception)
  const handleNusukFilterChange = (filterValues) => {
    setNusukFilters(filterValues);
    setCurrentPage(1);
  };

  // Fetch arrivals data
  const { count: _nusukCount, ...nusukFiltersValue } = nusukFilters;
  const params = isNusuk
    ? {
      port,
      ...nusukFiltersValue,
      page: currentPage,
      date: dateQuery,
    }
    : {
      port,
      search: debouncedValue,
      page: currentPage,
      filters,
      date: dateQuery,
    };
  const {
    data: arrivalsRes,
    isLoading,
    isFetching,
  } = useGetAirportArrivalsQuery(params);

  const arrivals = arrivalsRes?.data?.data || [];
  const meta = arrivalsRes?.data?.meta || {};
  const loading = isLoading;
  const { labelValues, filterFields, title } = useArrival(
    arrivalsRes?.data?.date
  );

  // Sync selectedArrival with fresh data when arrivals update.
  // Match id + type: campaign and group rows can share the same numeric id.
  useEffect(() => {
    if (selectedArrival && arrivals.length > 0) {
      const updatedArrival = arrivals.find(
        (arrival) =>
          arrival.id === selectedArrival.id &&
          (arrival?.type ?? "") === (selectedArrival?.type ?? "")
      );
      if (updatedArrival) {
        setSelectedArrival(updatedArrival);
      }
    }
  }, [arrivals, selectedArrival]);

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Status mapping for airport arrivals
  const getStatusConfig = (status) => {
    const successArrival = {
      color: "success",
      text: t("ARRIVAL_CARD_ARRIVED_AIRPORT"),
      icon: AirplaneLanding01Icon,
    };
    if (isNusuk) {
      delete successArrival.text;
      return successArrival;
    }
    const statusMap = {
      arrived_airport: successArrival,
      not_arrived: {
        color: "brand",
        text: t("ARRIVAL_CARD_NOT_ARRIVED"),
        icon: Airplane02Icon,
      },
      cancelled: {
        color: "danger",
        text: t("ARRIVAL_CARD_CANCELED"),
        icon: Alert02Icon,
      },
      late: {
        color: "danger",
        text: t("ARRIVAL_CARD_LATE"),
        icon: Alert02Icon,
      },
    };

    return statusMap[status] || statusMap.not_arrived;
  };

  // const isLate = (arrival) => {
  //   if (!arrival.scheduled_at) return false;
  //   // Get the current date and time
  //   const now = new Date();

  //   // Parse the flight_date (format: "21:30:00")
  //   const [hours, minutes, seconds] = arrival.scheduled_at
  //     .split(" ")[1]
  //     .split(":")
  //     .map(Number);

  //   // Create a datetime object for today with the flight_date
  //   const arrivalDateTime = new Date(
  //     now.getFullYear(),
  //     now.getMonth(),
  //     now.getDate(),
  //     hours,
  //     minutes,
  //     seconds || 0
  //   );

  //   // Compare current time with arrival time
  //   return now > arrivalDateTime && arrival.status === "not_arrived";
  // };

  // Get the effective status considering if arrival is late
  const getEffectiveStatus = (arrival) => {
    // // If status is not_arrived and current time has passed ArrTime, mark as late
    // if (arrival.status === "not_arrived" && isLate(arrival)) {
    //   return "late";
    // }
    // return arrival.status;
    const dateTimeNow= new Date();

    const arrivalDateTime = new Date(arrival.scheduled_at?.replace(" ", "T"));

    if(arrival.status === "not_arrived" && dateTimeNow > arrivalDateTime){
      return "late";
    }
    return arrival.status;
    

  };

  // Create action buttons for each arrival
  const createActions = (arrival, index) => {
    if (isNusuk && !canControlNusuk) return null;
    if (!isNusuk && !canControlDelegation) return null;

    const createReceptionCardButton = (
      <Button
        className="btn-primary flex-fill"
        onClick={() => {
          setSelectedArrival(arrival);
          setSelectedArrivalIndex(index);
          setIsModalOpen(true);
        }}
      >
        {t("ARRIVAL_CARD_CREATE_RECEPTION_CARD")}
      </Button>
    );

    const cancelFlightButton = (
      <Button
        className="btn-danger flex-fill"
        onClick={() => {
          setSelectedArrival(arrival);
          setIsCancelConfirmOpen(true);
        }}
        disabled={cancelFlightMutation.isPending}
      >
        {t("ARRIVAL_CARD_CANCEL_FLIGHT")}
      </Button>
    );

    const markArrivedFlight = (
      <Button
        className="btn-primary flex-fill"
        onClick={() => {
          setSelectedArrival(arrival);
          setIsConfirmOpen(true);
        }}
        disabled={markAsArrivedMutation.isPending}
      >
        {t("ARIVED_AIRPORT")}
      </Button>
    );

    const restoreFlight = (
      <Button
        onClick={() => {
          setSelectedArrival(arrival);
          setIsRestoreConfirmOpen(true);
        }}
        className="btn-outline btn-brand flex-fill"
        disabled={restoreFlightMutation.isPending}
      >
        {t("RESTORE_FLIGHT")}
      </Button>
    );

    switch (arrival.status) {
      case "arrived_airport":
        return (
          <>
            {createReceptionCardButton}
            {cancelFlightButton}
          </>
        );
      case "not_arrived":
        return (
          <>
            {markArrivedFlight}
            {cancelFlightButton}
          </>
        );
      case "cancelled":
        return <>{restoreFlight}</>;
      default:
        return (
          <>
            {markArrivedFlight}
            {cancelFlightButton}
          </>
        );
    }
  };

  // Create skeleton items for loading state
  const skeletonItems = Array.from({ length: 3 }, (_, index) => ({
    id: index,
  }));

  const onFilterApply = () => {
    setCurrentPage(1);
  };

  const onFilterReset = () => {
    setCurrentPage(1);
  };

  const options = [
    { label: `${t("TODAY")}`, value: "today" },
    { label: `${t("ALL")}`, value: "all" },
  ];

  const handleDateChange = (e) => {
    setDateQuery(e.target.value);
  };
  console.log("arrivals", arrivals);
  return (
    <Container fluid>
      <Stack gap={3}>
        {isNusuk ? (
          <Flex gap={8} align="center">
            <div className="flex-fill">
              <SearchFilter
                filterFields={filterFields}
                onChange={handleNusukFilterChange}
                debounced
              />
            </div>
          </Flex>
        ) : (
          <Stack direction="horizontal" gap={2}>
            <div className="flex-fill">
              <AntInput
                prefix={<HugeiconsIcon icon={Search01Icon} />}
                type="text"
                placeholder={t("SEARCH_HERE")}
                value={searchValue}
                onChange={handleSearchChange}
              />
            </div>
            <Flex gap={8}>
              <Badge
                count={Object.values(filters || {})?.filter?.(Boolean)?.length}
              >
                <BtnAsLink
                  className="btn-secondary"
                  icon={<HugeiconsIcon icon={FilterIcon} />}
                  onClick={() => setIsFilterOpen(true)}
                >
                  {t("FILTER")}
                </BtnAsLink>
              </Badge>
            </Flex>
          </Stack>
        )}
        <Flex justify="flex-start">
          {isNusuk && (
            <Radio.Group
              disabled={loading}
              onChange={handleDateChange}
              value={dateQuery}
              options={options}
            />
          )}
        </Flex>
        <Row className="flex-wrap">
          {loading ? (
            skeletonItems.map((item) => <ArrivalCardSkeleton key={item.id} />)
          ) : arrivals?.length === 0 ? (
            <div className="text-center py-5">
              <HugeiconsIcon size={40} icon={AirplaneLanding01Icon} />
              <h4 className="text-muted mb-2">
                {debouncedValue
                  ? t("NO_SEARCH_RESULTS") || "لا توجد نتائج للبحث"
                  : t("NO_ARRIVALS_FOUND") || "لا يوجد وصول"}
              </h4>
              <p className="text-muted mb-0">
                {debouncedValue
                  ? t("NO_SEARCH_RESULTS_DESCRIPTION") ||
                  "لم يتم العثور على وصولات تطابق البحث"
                  : t("NO_ARRIVALS_DESCRIPTION") || "لم يتم العثور على أي وصول"}
              </p>
            </div>
          ) : (
            arrivals?.map((arrival, index) => {
              const effectiveStatus = getEffectiveStatus(arrival);

              return (
                <ArrivalCard
                  key={Object.values(arrival).join("-")}
                  title={isNusuk ? title[source] : title[arrival?.type]}
                  titleValue={
                    arrival?.report_number ??
                    arrival?.request_number ??
                    arrival?.serial ??
                    "-"
                  }
                  groupName={arrival?.name}
                  arrival={arrival}
                  statusConfig={getStatusConfig(effectiveStatus)}
                  actions={!isNusuk && createActions(arrival, index)}
                  labelValues={labelValues}
                  extra={
                    !isNusuk && (
                      <BusStatusSection
                        label="BUS_DEPARTURE_STATUS"
                        count={
                          arrival?.bus_cards?.filter(
                            (bus) => !!bus.departure_time
                          ).length
                        }
                        totalCount={arrival?.bus_cards?.length}
                      />
                    )
                  }
                />
              );
            })
          )}
        </Row>
        <AntPagination
          className="sticky-bottom z-1 w-100 p-3 bg-white"
          hideOnSinglePage
          pageSize={meta.per_page || 10}
          current={currentPage}
          onChange={handlePageChange}
          total={meta.total || 0}
          showSizeChanger={false}
        />
      </Stack>

      {/* Reception Card Modal */}
      {selectedArrival && (
        <ReceptionCardModal
          params={params}
          selectedArrivalIndex={selectedArrivalIndex}
          isOpen={isModalOpen}
          toggle={() => {
            setIsModalOpen(false);
            setSelectedArrival(null);
          }}
          arrival={selectedArrival}
          date={arrivalsRes?.data?.date}
        />
      )}

      {/* Mark as Arrived Confirm Modal */}
      {selectedArrival && (
        <ConfirmModal
          isOpen={isConfirmOpen}
          onToggle={() => {
            if (!markAsArrivedMutation.isPending) {
              setIsConfirmOpen(false);
              setSelectedArrival(null);
            }
          }}
          onConfirm={() => {
            markAsArrivedMutation.mutate(
              {
                campaign_id: selectedArrival.id,
                id: selectedArrival.id,
                type: selectedArrival.type,
              },
              {
                onSuccess: () => {
                  setIsConfirmOpen(false);
                  setSelectedArrival(null);
                },
              }
            );
          }}
          title={t("CONFIRM")}
          message={
            <Trans
              i18nKey="CONFIRM_MARK_ARRIVED"
              components={{
                green: (
                  <span style={{ color: "#166433", fontWeight: "bold" }} />
                ),
              }}
            />
          }
          confirmText={t("CONFIRM_BUTTON")}
          confirmVariant="primary"
          confirmIcon={AirplaneLanding01Icon}
          isLoading={markAsArrivedMutation.isPending}
        />
      )}

      {/* Cancel Flight Confirm Modal */}
      {selectedArrival && (
        <ConfirmModal
          isOpen={isCancelConfirmOpen}
          onToggle={() => {
            if (!cancelFlightMutation.isPending) {
              setIsCancelConfirmOpen(false);
              setSelectedArrival(null);
            }
          }}
          onConfirm={() => {
            cancelFlightMutation.mutate(
              {
                campaign_id: selectedArrival.id,
                id: selectedArrival.id,
              },
              {
                onSuccess: () => {
                  setIsCancelConfirmOpen(false);
                  setSelectedArrival(null);
                },
              }
            );
          }}
          title={t("CONFIRM")}
          message={
            <Trans
              i18nKey="CONFIRM_CANCEL_FLIGHT"
              components={{
                red: <span className="text-danger fw-bold" />,
              }}
            />
          }
          confirmText={t("CONFIRM_BUTTON")}
          confirmVariant="primary"
          confirmIcon={Alert02Icon}
          isLoading={cancelFlightMutation.isPending}
        />
      )}

      {/* Restore Flight Confirm Modal */}
      {selectedArrival && (
        <ConfirmModal
          isOpen={isRestoreConfirmOpen}
          onToggle={() => {
            if (!restoreFlightMutation.isPending) {
              setIsRestoreConfirmOpen(false);
              setSelectedArrival(null);
            }
          }}
          onConfirm={() => {
            restoreFlightMutation.mutate(
              {
                campaign_id: selectedArrival.id,
                id: selectedArrival.id,
              },
              {
                onSuccess: () => {
                  setIsRestoreConfirmOpen(false);
                  setSelectedArrival(null);
                },
              }
            );
          }}
          title={t("CONFIRM")}
          message={
            t("CONFIRM_RESTORE_FLIGHT") || "هل أنت متأكد من استعادة هذه الرحلة؟"
          }
          confirmText={t("CONFIRM_BUTTON")}
          confirmVariant="primary"
          confirmIcon={Airplane02Icon}
          isLoading={restoreFlightMutation.isPending}
        />
      )}
      {!isNusuk && (
        <GenericFilterModal
          isOpen={isFilterOpen}
          toggle={() => setIsFilterOpen(false)}
          onApply={handleFilterApply(onFilterApply)}
          onReset={handleFilterReset(onFilterReset)}
          filterFields={filterFields}
          filters={filters}
        />
      )}
    </Container>
  );
};

export default AirportReceptionList;
